import type { MaskedGameState, RoomErrorPayload, RoomStatePayload } from '@completto/shared';
import { GameStatsView } from '../components/Result/GameStats.ts';
import { PlayerScoreListView } from '../components/Result/PlayerScoreList.ts';
import { ResultSummaryView } from '../components/Result/ResultSummary.ts';
import { successImpact } from '../lib/haptics.ts';
import { pushToast } from '../stores/index.ts';

/**
 * 외부 GameState 변경 구독으로 자동 이동하는 그룹.
 * IN_PROGRESS 수신 시 onGameRestarted 호출 → router.navigate(play).
 * subscribe와 callback은 항상 함께 주입돼야 의미 있음 (멀티 모드에서만 사용).
 */
export type AutoNavigateOnRestart = {
  subscribeGameState: (fn: (state: MaskedGameState) => void) => () => void;
  onGameRestarted: () => void;
};

export type ResultPageActions = {
  amHost: boolean;
  onGoMain: () => void;
  /** 호스트만 노출. 누르면 같은 룸/같은 멤버로 새 게임 시작 요청. */
  onRestart?: () => void;
  /** 재시작 버튼 라벨. 기본값 "같은 룸 재시작" (멀티 호스트 기준). */
  restartLabel?: string;
  /** 멀티 모드 전용. 호스트가 재시작 누르면 비호스트들도 자동 play로 이동. */
  autoNavigateOnRestart?: AutoNavigateOnRestart;
  /**
   * 멀티 모드 전용. RoomState 변화를 구독하여 호스트 이양/이탈을 감지한다.
   * 호스트가 떠나면 게스트가 결과 화면에 갇히지 않고 새 호스트 권한 또는 자동 메인 이동을 받도록.
   */
  subscribeRoomState?: (fn: (state: RoomStatePayload) => void) => () => void;
  /**
   * 멀티 모드 전용. 서버의 ROOM_ERROR 이벤트를 구독한다.
   * 정책 C: FINISHED 룸에서 호스트가 떠나면 HOST_LEFT 수신 → 토스트 + 자동 메인.
   */
  subscribeError?: (fn: (err: RoomErrorPayload) => void) => () => void;
};

const LEFT_ALONE_NAVIGATE_DELAY_MS = 2000;

function formatMs(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export class ResultPage {
  private root: HTMLElement;
  private state: MaskedGameState;
  private myPlayerId: string;
  private actions: ResultPageActions;
  private amHost: boolean;
  private unsubscribeGame: (() => void) | null = null;
  private unsubscribeRoom: (() => void) | null = null;
  private unsubscribeError: (() => void) | null = null;
  private leftAloneTimer: ReturnType<typeof setTimeout> | null = null;
  private restartSent = false;
  private lastHostId: string | null = null;
  private lastPlayerIds: ReadonlySet<string> = new Set();
  private hostConnected = true;
  private restartDeadline: number | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    root: HTMLElement,
    state: MaskedGameState,
    myPlayerId: string,
    actions: ResultPageActions,
  ) {
    this.root = root;
    this.state = state;
    this.myPlayerId = myPlayerId;
    this.actions = actions;
    this.amHost = actions.amHost;
  }

  mount(): void {
    const autoNav = this.actions.autoNavigateOnRestart;
    if (autoNav) {
      this.unsubscribeGame = autoNav.subscribeGameState((next) => {
        if (next.status === 'IN_PROGRESS') {
          autoNav.onGameRestarted();
        }
      });
    }
    if (this.actions.subscribeRoomState) {
      this.unsubscribeRoom = this.actions.subscribeRoomState((room) => {
        this.handleRoomStateChange(room);
      });
    }
    if (this.actions.subscribeError) {
      this.unsubscribeError = this.actions.subscribeError((err) => {
        this.handleServerError(err);
      });
    }
    if (this.state.winnerId === this.myPlayerId) {
      void successImpact();
    }
    this.render();
  }

  unmount(): void {
    this.unsubscribeGame?.();
    this.unsubscribeGame = null;
    this.unsubscribeRoom?.();
    this.unsubscribeRoom = null;
    this.unsubscribeError?.();
    this.unsubscribeError = null;
    if (this.leftAloneTimer !== null) {
      clearTimeout(this.leftAloneTimer);
      this.leftAloneTimer = null;
    }
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.root.innerHTML = '';
  }

  private handleServerError(err: RoomErrorPayload): void {
    if (err.code === 'HOST_LEFT') {
      pushToast(err.message, 'warn');
      this.scheduleGoMain();
    }
  }

  private handleRoomStateChange(room: RoomStatePayload): void {
    const me = room.players.find((p) => p.id === this.myPlayerId);
    const playerIds = new Set(room.players.map((p) => p.id));
    const prevPlayerIds = this.lastPlayerIds;
    const prevHostId = this.lastHostId;
    this.lastPlayerIds = playerIds;
    this.lastHostId = room.hostId;

    // 내가 룸에서 빠진 경우 (강퇴/방 해체) — 안내 후 메인 이동.
    if (!me) {
      pushToast('방이 종료되어 메인으로 돌아갑니다', 'warn');
      this.scheduleGoMain();
      return;
    }

    // 나 외에 다른 플레이어가 모두 떠난 경우 — 결과 화면에 혼자 있을 이유 없음.
    if (room.players.length <= 1) {
      pushToast('상대방이 모두 방을 떠났습니다', 'warn');
      this.scheduleGoMain();
      return;
    }

    // 직전 RoomState 대비 사라진 플레이어 중 직전 호스트가 있으면 호스트 교체 안내.
    if (prevPlayerIds.size > 0 && prevHostId && !playerIds.has(prevHostId)) {
      const newHost = room.players.find((p) => p.id === room.hostId);
      const newHostName =
        newHost?.id === this.myPlayerId ? '나' : (newHost?.nickname ?? '다른 플레이어');
      pushToast(`호스트가 방을 떠나 ${newHostName}님이 새 호스트가 되었습니다`, 'info');
    }

    // 내 호스트 상태가 바뀌면 액션 영역 리렌더 — 재시작 버튼 노출/숨김 갱신.
    const nextAmHost = me.isHost;
    if (nextAmHost !== this.amHost) {
      this.amHost = nextAmHost;
      this.restartSent = false;
      this.rerenderActions();
    }

    // 호스트의 연결 상태 추적 — 내가 호스트가 아닐 때만 의미 있음.
    // 잠수 시 결과 화면 상단에 경고 배너 노출, 복귀 시 제거.
    const host = room.hostId ? room.players.find((p) => p.id === room.hostId) : null;
    const hostConnectedNow = host?.connected ?? true;
    if (host && !me.isHost && hostConnectedNow !== this.hostConnected) {
      this.hostConnected = hostConnectedNow;
      if (hostConnectedNow) {
        pushToast(`호스트(${host.nickname})가 복귀했습니다`, 'info');
      } else {
        pushToast(`호스트(${host.nickname})의 연결이 끊겼습니다`, 'warn');
      }
      this.updateHostDisconnectedBanner(hostConnectedNow ? null : host.nickname);
    } else if (me.isHost) {
      // 내가 호스트가 되면 (또는 처음부터 호스트면) 배너는 의미 없음.
      this.hostConnected = true;
      this.updateHostDisconnectedBanner(null);
    }

    // 결정 마감 시각 동기화 — RoomState가 도착할 때마다 deadline 갱신.
    this.syncRestartDeadline(room.restartDeadline);
  }

  private scheduleGoMain(): void {
    if (this.leftAloneTimer !== null) return;
    this.leftAloneTimer = setTimeout(() => {
      this.leftAloneTimer = null;
      this.actions.onGoMain();
    }, LEFT_ALONE_NAVIGATE_DELAY_MS);
  }

  /**
   * 호스트가 잠수 중일 때 결과 화면 상단에 노출되는 경고 배너를 토글한다.
   * nickname이 null이면 배너를 제거(호스트 복귀/내가 호스트가 됨 등).
   */
  private updateHostDisconnectedBanner(nickname: string | null): void {
    const inner = this.root.querySelector<HTMLElement>('[data-role="result-inner"]');
    if (!inner) return;
    const existing = inner.querySelector<HTMLElement>('[data-role="host-disconnected"]');
    if (nickname === null) {
      existing?.remove();
      return;
    }
    if (existing) {
      const nameEl = existing.querySelector<HTMLElement>('[data-role="host-name"]');
      if (nameEl) nameEl.textContent = nickname;
      return;
    }
    const banner = document.createElement('div');
    banner.dataset.role = 'host-disconnected';
    banner.className =
      'w-full rounded-xl bg-rose-900/30 ring-1 ring-rose-600/70 px-4 py-3 flex items-start gap-3 text-rose-100';
    banner.innerHTML = `
      <div class="text-xl leading-none" aria-hidden="true">⚠</div>
      <div class="flex-1 text-sm leading-relaxed">
        <strong class="font-semibold">호스트(<span data-role="host-name"></span>)의 연결이 끊겼습니다.</strong>
        <div class="text-rose-200/80 mt-0.5">30초 내 복귀하지 않으면 게임이 종료됩니다.</div>
      </div>
    `;
    const nameEl = banner.querySelector<HTMLElement>('[data-role="host-name"]');
    if (nameEl) nameEl.textContent = nickname;
    inner.insertBefore(banner, inner.firstChild);
  }

  /**
   * 서버가 보내준 결정 마감 시각을 반영. deadline이 새로 들어오거나 null로 바뀌면
   * 카운트다운 타이머를 (재)시작/정지한다.
   */
  private syncRestartDeadline(deadline: number | null): void {
    if (deadline === this.restartDeadline) {
      this.updateCountdownDisplay();
      return;
    }
    this.restartDeadline = deadline;
    if (deadline === null) {
      if (this.countdownTimer !== null) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    } else if (this.countdownTimer === null) {
      this.countdownTimer = setInterval(() => this.updateCountdownDisplay(), 1000);
    }
    this.updateCountdownDisplay();
  }

  /**
   * 카운트다운 표시 갱신. 잠수 배너 노출 중이거나 deadline 없거나 0 이하면 숨김.
   * 호스트/게스트에 따라 카피가 다르다.
   */
  private updateCountdownDisplay(): void {
    const inner = this.root.querySelector<HTMLElement>('[data-role="result-inner"]');
    if (!inner) return;
    const existing = inner.querySelector<HTMLElement>('[data-role="restart-deadline"]');
    const hasBanner = inner.querySelector<HTMLElement>('[data-role="host-disconnected"]') !== null;
    const remainingMs = this.restartDeadline === null ? 0 : this.restartDeadline - Date.now();
    if (this.restartDeadline === null || remainingMs <= 0 || hasBanner) {
      existing?.remove();
      if (this.restartDeadline !== null && remainingMs <= 0 && this.countdownTimer !== null) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
      return;
    }

    const text = this.amHost
      ? `결정 시간 ${formatMs(remainingMs)} 남음`
      : `호스트 결정 대기 ${formatMs(remainingMs)}`;

    if (existing) {
      const timeEl = existing.querySelector<HTMLElement>('[data-role="deadline-text"]');
      if (timeEl) timeEl.textContent = text;
      return;
    }

    const el = document.createElement('div');
    el.dataset.role = 'restart-deadline';
    el.className =
      'w-full flex items-center justify-center gap-2 text-caption text-wood-coffee/80 tabular-nums';
    el.innerHTML = `
      <span aria-hidden="true">⏱</span>
      <span data-role="deadline-text"></span>
    `;
    const textEl = el.querySelector<HTMLElement>('[data-role="deadline-text"]');
    if (textEl) textEl.textContent = text;
    // 잠수 배너 다음, summary 위에 끼워넣는다. 둘 다 없으면 main의 맨 앞.
    const banner = inner.querySelector<HTMLElement>('[data-role="host-disconnected"]');
    if (banner) banner.after(el);
    else inner.insertBefore(el, inner.firstChild);
  }

  private rerenderActions(): void {
    const old = this.root.querySelector<HTMLElement>('[data-role="actions"]');
    if (!old) return;
    const fresh = this.renderActions();
    fresh.classList.add('result-stagger');
    fresh.style.setProperty('--result-delay', '0ms');
    old.replaceWith(fresh);
  }

  private render(): void {
    this.root.innerHTML = '';
    const main = document.createElement('main');
    main.className = 'min-h-screen safe-x safe-y flex flex-col items-center';
    main.style.setProperty('--page-pad-x', '1.5rem');
    main.style.setProperty('--page-pad-y', '1.5rem');
    const inner = document.createElement('div');
    inner.className = 'w-full max-w-md flex flex-col items-center gap-6';
    inner.dataset.role = 'result-inner';
    main.appendChild(inner);
    this.root.appendChild(main);

    const winner = this.findWinner();
    const summary = new ResultSummaryView({
      winnerNickname: winner?.nickname ?? '???',
      isMe: winner?.id === this.myPlayerId,
    });
    const summaryEl = summary.render();
    summaryEl.classList.add('result-stagger');
    summaryEl.style.setProperty('--result-delay', '0ms');
    inner.appendChild(summaryEl);

    const stats = new GameStatsView({ metrics: this.state.metrics });
    const statsEl = stats.render();
    statsEl.classList.add('result-stagger');
    statsEl.style.setProperty('--result-delay', '250ms');
    inner.appendChild(statsEl);

    const heading = document.createElement('h2');
    heading.className = 'text-sm text-wood-coffee/70 self-start result-stagger';
    heading.style.setProperty('--result-delay', '500ms');
    heading.textContent = '플레이어 진행도';
    inner.appendChild(heading);

    const scores = new PlayerScoreListView({
      players: this.state.players,
      myPlayerId: this.myPlayerId,
      winnerId: this.state.winnerId,
      staggerStartMs: 600,
    });
    const scoresEl = scores.render();
    scoresEl.classList.add('w-full');
    inner.appendChild(scoresEl);

    const actionsEl = this.renderActions();
    actionsEl.classList.add('result-stagger');
    const totalScoreDelay = 600 + this.state.players.length * 150;
    actionsEl.style.setProperty('--result-delay', `${totalScoreDelay}ms`);
    inner.appendChild(actionsEl);
  }

  private renderActions(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'w-full flex flex-col gap-3 mt-2';
    wrap.dataset.role = 'actions';

    if (this.actions.onRestart && this.amHost) {
      const restartBtn = document.createElement('button');
      restartBtn.type = 'button';
      restartBtn.dataset.role = 'restart';
      restartBtn.className =
        'rounded-xl bg-card-navy hover:bg-card-deep disabled:bg-wood-sand/40 disabled:text-wood-coffee/55 text-card-gold py-3 font-bold';
      restartBtn.textContent = this.actions.restartLabel ?? '같은 룸 재시작';
      restartBtn.addEventListener('click', () => this.handleRestartClick(restartBtn));
      wrap.appendChild(restartBtn);
    }

    const mainBtn = document.createElement('button');
    mainBtn.type = 'button';
    mainBtn.dataset.role = 'main';
    mainBtn.className =
      'rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white py-3 font-semibold';
    mainBtn.textContent = '메인';
    mainBtn.addEventListener('click', () => this.actions.onGoMain());
    wrap.appendChild(mainBtn);

    return wrap;
  }

  private handleRestartClick(button: HTMLButtonElement): void {
    if (this.restartSent) return;
    if (!this.actions.onRestart) return;
    this.restartSent = true;
    button.disabled = true;
    button.textContent = '시작 중…';
    this.actions.onRestart();
  }

  private findWinner() {
    if (!this.state.winnerId) return null;
    return this.state.players.find((p) => p.id === this.state.winnerId) ?? null;
  }
}
